import type { CatalogProductDto } from "@island/catalog-core";
import { useEffect, useRef, useState } from "react";

import { useSession } from "../session/SessionContext.js";

/**
 * The `/product` command.
 *
 * An agent asked for a product link mid-conversation has two bad options without
 * this: leave the dashboard to find the URL, or paste one from memory. The first
 * costs the customer a minute of silence; the second is how a customer ends up with
 * a link to a discontinued variant.
 *
 * So the composer watches for `/product ` at the start of a line and turns the rest
 * into a search. What the agent picks is attached by **id** — the API builds the card
 * from the catalogue — so nothing the dashboard renders here can put a wrong price
 * into a customer's transcript.
 */

/** Long enough to swallow a burst of typing, short enough to feel immediate. */
const DEBOUNCE_MS = 200;

const MAX_RESULTS = 6;

export interface ProductPickerProps {
  /** The text after `/product`. Empty shows the first products rather than nothing. */
  readonly query: string;
  readonly onPick: (product: CatalogProductDto) => void;
  readonly onDismiss: () => void;
}

export function ProductPicker({ query, onPick, onDismiss }: ProductPickerProps) {
  const { api } = useSession();
  const [results, setResults] = useState<readonly CatalogProductDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);

  /**
   * Which request the state belongs to.
   *
   * Two searches in flight can return out of order, and without this the slower,
   * older one wins — the agent sees results for a word they have finished deleting.
   */
  const requestId = useRef(0);

  useEffect(() => {
    const timer = setTimeout(() => {
      const id = requestId.current + 1;
      requestId.current = id;
      setLoading(true);

      const trimmed = query.trim();
      const path =
        trimmed === ""
          ? `/catalog/products?limit=${String(MAX_RESULTS)}`
          : `/catalog/products?limit=${String(MAX_RESULTS)}&search=${encodeURIComponent(trimmed)}`;

      void api
        .get<{ products: readonly CatalogProductDto[] }>(path)
        .then((page) => {
          if (requestId.current !== id) return;
          setResults(page.products);
          setFailed(false);
        })
        .catch(() => {
          if (requestId.current === id) setFailed(true);
        })
        .finally(() => {
          if (requestId.current === id) setLoading(false);
        });
    }, DEBOUNCE_MS);

    return () => {
      clearTimeout(timer);
    };
  }, [api, query]);

  return (
    <div className="product-picker" role="listbox" aria-label="Products">
      {loading && results.length === 0 && <p className="muted small">Searching…</p>}

      {!loading && results.length === 0 && (
        <p className="muted small">
          {failed ? "Couldn't reach the catalogue." : "No products match that."}
        </p>
      )}

      {results.map((product) => (
        <button
          key={product.productId}
          type="button"
          role="option"
          aria-selected="false"
          className="product-option"
          onClick={() => {
            onPick(product);
          }}
        >
          {product.imageUrl !== undefined && <img src={product.imageUrl} alt="" />}
          <span>
            <strong>{product.name}</strong>
            {/* The agent needs to see this before sending, not after: offering a
                sold-out product is the mistake this picker exists to prevent. */}
            {product.availability === "out_of_stock" && (
              <span className="small error"> · out of stock</span>
            )}
          </span>
        </button>
      ))}

      <button type="button" className="small muted product-dismiss" onClick={onDismiss}>
        Cancel
      </button>
    </div>
  );
}
